import { Link } from 'react-router-dom';
import React from 'react';
import { Form, Input, Button, message, Typography, Checkbox } from 'antd';
import { UserOutlined, LockOutlined } from '@ant-design/icons';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import type { FormProps } from 'antd';

import { Service, OpenAPI } from '@/apis';
import type { LoginDto } from '@/apis';

import styles from './index.module.less';

const { Title, Text } = Typography;

interface LoginFormValues {
  userName: string;
  password: string;
  remember: boolean;
}

const Login: React.FC = () => {
  const navigate = useNavigate();
  const [form] = Form.useForm();

  useEffect(() => {
    const savedUserInfo = localStorage.getItem('rememberedUserInfo');
    if (savedUserInfo) {
      try {
        const { userName, password, remember } = JSON.parse(savedUserInfo);
        form.setFieldsValue({
          userName,
          password,
          remember: remember || false,
        });
      } catch (error) {
        console.error('解析保存的用户信息失败:', error);
      }
    }
  }, [form]);

  const onFinish: FormProps<LoginFormValues>['onFinish'] = values => {
    const loginData: LoginDto = {
      managerName: values.userName,
      password: values.password,
    };

    Service.managerControllerLogins(loginData)
      .then((response: any) => {
        let token = null;
        if (response?.token) {
          token = response.token;
        } else if (response?.data?.token) {
          token = response.data.token;
        } else if (typeof response === 'object' && 'token' in response) {
          token = (response as any).token;
        }

        if (token) {
          localStorage.setItem('token', token);
          OpenAPI.TOKEN = token;
        } else {
          console.warn('登录响应中未找到 token，请检查 API 响应格式');
        }

        if (values.remember) {
          localStorage.setItem(
            'rememberedUserInfo',
            JSON.stringify({
              userName: values.userName,
              password: values.password,
              remember: true,
            })
          );
        } else {
          localStorage.removeItem('rememberedUserInfo');
        }

        navigate('/admin');
        message.success('登录成功');
      })
      .catch((error: any) => {
        console.error('登录失败:', error);
        let errorMessage = '登录失败';

        const isCorsError =
          (!error?.response && error?.request) ||
          error?.message?.toLowerCase().includes('cors') ||
          error?.message?.toLowerCase().includes('cross-origin') ||
          error?.code === 'ERR_NETWORK' ||
          (error?.request && error.request.status === 0);

        if (isCorsError) {
          errorMessage = '跨域请求失败，请检查服务器 CORS 配置或联系管理员';
        } else if (error?.response) {
          if (error.response.status === 401) {
            errorMessage = '用户名或密码错误';
          } else if (error.response.status === 403) {
            errorMessage = '没有权限访问';
          } else if (error.response.status === 404) {
            errorMessage = '接口不存在';
          } else if (error.response.status >= 500) {
            errorMessage = '服务器错误，请稍后重试';
          } else if (error.response.data?.msg) {
            errorMessage = error.response.data.msg;
          } else if (error.response.data?.errstr) {
            errorMessage = error.response.data.errstr;
          }
        } else if (error?.request) {
          errorMessage = '网络请求失败，请检查网络连接';
        } else {
          errorMessage = error?.message || '登录失败，请重试';
        }
        message.error(errorMessage);
      });
  };

  const onFinishFailed: FormProps<LoginFormValues>['onFinishFailed'] = () => {};

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.headerInner}>
          <Link to='/' className={styles.logo}>
            YingC Enrich
          </Link>
          <Text className={styles.headerTip}>Admin Login</Text>
        </div>
      </header>

      <section className={styles.hero}>
        <div className={styles.heroInner}>
          <div className={styles.formCard}>
            <div className={styles.formHeader}>
              <Title level={2} className={styles.formTitle}>
                Admin Login
              </Title>
              <Text className={styles.formSubtitle}>欢迎登录管理后台</Text>
            </div>

            <Form<LoginFormValues>
              form={form}
              className={styles.form}
              name='login'
              layout='vertical'
              size='large'
              initialValues={{ remember: false }}
              onFinish={onFinish}
              onFinishFailed={onFinishFailed}
              autoComplete='off'
            >
              <Form.Item
                label='用户名'
                name='userName'
                rules={[
                  { required: true, message: '请输入用户名' },
                  { min: 2, message: '用户名至少 2 个字符' },
                ]}
              >
                <Input
                  prefix={<UserOutlined className={styles.inputPrefix} />}
                  placeholder='请输入用户名'
                  className={styles.input}
                />
              </Form.Item>

              <Form.Item
                label='密码'
                name='password'
                rules={[
                  { required: true, message: '请输入密码' },
                  { min: 6, message: '密码至少 6 个字符' },
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined className={styles.inputPrefix} />}
                  placeholder='请输入密码'
                  className={styles.input}
                />
              </Form.Item>

              <Form.Item
                name='remember'
                valuePropName='checked'
                className={styles.rememberItem}
              >
                <Checkbox className={styles.rememberCheckbox}>
                  记住密码
                </Checkbox>
              </Form.Item>

              <Form.Item className={styles.submitItem}>
                <Button
                  type='primary'
                  htmlType='submit'
                  className={styles.submitBtn}
                  block
                  size='large'
                >
                  登录
                </Button>
              </Form.Item>
            </Form>
          </div>

          <p className={styles.heroBack}>
            <Link to='/'>← 返回首页</Link>
          </p>
        </div>
      </section>

      <footer className={styles.footer}>
        <div className={styles.footerInner}>
          © {new Date().getFullYear()} YingC Enrich. All rights reserved.
        </div>
      </footer>
    </div>
  );
};

export default Login;
